import { GivingTypeEnum } from 'models';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from 'permissions';

const organizationMembershipTypes = {
  church: new Set<'all' | GivingTypeEnum>([
    'all',
    GivingTypeEnum.CROWDFUNDING,
    GivingTypeEnum.OFFERING,
    GivingTypeEnum.TITHE,
  ]),
  nonProfit: new Set<'all' | GivingTypeEnum>(['all', GivingTypeEnum.CROWDFUNDING, GivingTypeEnum.DONATION]),
};

export const useGivingTypeOptions = () => {
  const { t } = useTranslation();

  const { canViewNonProfitGiving, canViewProfitGiving } = usePermissions();

  const locales: Record<GivingTypeEnum, string> = useMemo(
    () => ({
      [GivingTypeEnum.CROWDFUNDING]: t('Giving crowdfunding'),
      [GivingTypeEnum.DONATION]: t('Giving donation'),
      [GivingTypeEnum.OFFERING]: t('Giving offering'),
      [GivingTypeEnum.TITHE]: t('Giving tithe'),
    }),
    [t],
  );

  const options: Array<{
    label: string;
    value: 'all' | GivingTypeEnum;
  }> = useMemo(
    () => [
      {
        label: t('All'),
        value: 'all',
      },
      {
        label: locales[GivingTypeEnum.OFFERING],
        value: GivingTypeEnum.OFFERING,
      },
      {
        label: locales[GivingTypeEnum.DONATION],
        value: GivingTypeEnum.DONATION,
      },
      {
        label: locales[GivingTypeEnum.TITHE],
        value: GivingTypeEnum.TITHE,
      },
      {
        label: locales[GivingTypeEnum.CROWDFUNDING],
        value: GivingTypeEnum.CROWDFUNDING,
      },
    ],
    [locales, t],
  );

  const getOptionsBasedOnPermissions = useCallback(() => {
    const hasNonProfitTypes = canViewNonProfitGiving({});
    const hasProfitTypes = canViewProfitGiving({});
    if (hasNonProfitTypes && hasProfitTypes) {
      return options;
    }
    if (hasNonProfitTypes) {
      return options.filter((type) => organizationMembershipTypes.nonProfit.has(type.value));
    }
    return options.filter((type) => organizationMembershipTypes.church.has(type.value));
  }, [options, canViewNonProfitGiving, canViewProfitGiving]);

  const getTranslate = useCallback((key: GivingTypeEnum) => locales[key], [locales]);

  return useMemo(
    () => ({
      getOptionsBasedOnPermissions,
      getTranslate,
      options,
    }),
    [getOptionsBasedOnPermissions, getTranslate, options],
  );
};
